import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Home,
  Building2,
  PlugZap,
  BatteryCharging,
  Lightbulb,
  Building,
  Droplet,
  ArrowRight,
  ChevronLeft,
  ChevronRight,
  Check,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { useTranslation } from "@/context/LanguageContext";

const ICONS = {
  rooftop: Home,
  commercial: Building2,
  ev: PlugZap,
  bess: BatteryCharging,
  street: Lightbulb,
  society: Building,
  heater: Droplet,
};

// Real installation photos (Unsplash CDN — verified 200 OK at build time)
const IMAGES = {
  // Residential rooftop with solar panels
  rooftop:
    "https://images.unsplash.com/photo-1509391366360-2e959784a276?w=2000&q=85&auto=format&fit=crop",
  // Aerial of factory/commercial building rooftop with solar
  commercial:
    "https://images.unsplash.com/photo-1504762300654-109d62172feb?w=2000&q=85&auto=format&fit=crop",
  // Electric vehicle plugged in to charging station
  ev: "https://images.unsplash.com/photo-1744973270493-01a1f5e5e1c4?w=2000&q=85&auto=format&fit=crop",
  // Large solar+battery storage (BESS)
  bess: "https://images.unsplash.com/photo-1589276534126-adef63a95e05?w=2000&q=85&auto=format&fit=crop",
  // Solar street light against bright blue sky
  street:
    "https://images.unsplash.com/photo-1740805276608-ef60e2e468ba?w=2000&q=85&auto=format&fit=crop",
  // Apartment / housing society complex
  society:
    "https://images.unsplash.com/photo-1545324418-cc1a3fa10c00?w=2000&q=85&auto=format&fit=crop",
  // Rooftop solar water heaters (evacuated tubes)
  heater:
    "https://images.unsplash.com/photo-1770178995777-31f3af9e6aa2?w=2000&q=85&auto=format&fit=crop",
};

const AUTO_MS = 6000;

export default function ServicesCarousel({ onRequestQuote, isOpening = false }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const items = t("services.items");
  const [idx, setIdx] = useState(0);
  const [paused, setPaused] = useState(false);
  const trackRef = useRef(null);

  const DEEP_LINKS = {
    rooftop: "/residential",
    commercial: "/commercial",
    society: "/housing-societies",
  };

  const handleCta = (s) => {
    const path = DEEP_LINKS[s.key];
    if (path) {
      navigate(path);
      window.scrollTo({ top: 0, behavior: "instant" });
    } else {
      onRequestQuote?.({ source: `service_${s.key}` });
    }
  };

  useEffect(() => {
    if (paused) return;
    const id = setInterval(() => setIdx((i) => (i + 1) % items.length), AUTO_MS);
    return () => clearInterval(id);
  }, [items.length, paused]);

  const goto = (i) => setIdx(((i % items.length) + items.length) % items.length);
  const next = () => goto(idx + 1);
  const prev = () => goto(idx - 1);

  return (
    <section
      id="services"
      data-testid="services-section"
      className={`relative w-full warm-bg ${isOpening ? "pt-[100px] pb-12 md:pb-16" : "py-14 md:py-20 border-y border-stone-200"}`}
    >
      {/* ambient blobs */}
      <div className="pointer-events-none absolute top-32 -left-24 w-72 h-72 rounded-full bg-green-200/40 blur-3xl" />
      <div className="pointer-events-none absolute bottom-24 -right-24 w-80 h-80 rounded-full bg-orange-200/30 blur-3xl" />

      {/* Heading row */}
      <div className="relative max-w-7xl mx-auto px-5 md:px-10">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-5 mb-6 md:mb-8">
          <div className="max-w-2xl">
            <span className="text-orange-600 font-semibold tracking-widest text-xs uppercase">
              {t("services.eyebrow")}
            </span>
            <h1 className="mt-2 font-display font-extrabold text-3xl md:text-4xl lg:text-5xl text-stone-900 leading-[1.05]">
              {t("services.title_pre")}{" "}
              <span className="text-green-700">{t("services.title_hl")}</span>{" "}
              {t("services.title_post")}
            </h1>
            <p className="mt-2 text-stone-600 leading-relaxed text-[15px] hidden md:block">
              {t("services.subtitle")}
            </p>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={prev}
              aria-label="Previous service"
              data-testid="services-prev"
              className="w-11 h-11 grid place-items-center rounded-full border border-stone-300 bg-white hover:bg-stone-50 hover:border-green-400 transition shadow-sm"
            >
              <ChevronLeft className="w-5 h-5" />
            </button>
            <button
              onClick={next}
              aria-label="Next service"
              data-testid="services-next"
              className="w-11 h-11 grid place-items-center rounded-full border border-stone-300 bg-white hover:bg-stone-50 hover:border-green-400 transition shadow-sm"
            >
              <ChevronRight className="w-5 h-5" />
            </button>
          </div>
        </div>
      </div>

      {/* Rounded carousel shell — visually nested in the page */}
      <div className="relative max-w-7xl mx-auto px-5 md:px-10">
        <div
          className="relative w-full overflow-hidden rounded-[28px] md:rounded-[32px] glass-panel premium-shadow"
          onMouseEnter={() => setPaused(true)}
          onMouseLeave={() => setPaused(false)}
        >
          <div
            ref={trackRef}
            className="flex transition-transform duration-700 ease-out"
            style={{ transform: `translateX(-${idx * 100}%)` }}
            data-testid="services-track"
          >
            {items.map((s, i) => {
              const Icon = ICONS[s.key] || Home;
              const img = IMAGES[s.key];
              const bullets = Array.isArray(s.bullets) ? s.bullets : null;
              return (
                <div key={s.key} className="min-w-full">
                  <div
                    data-testid={`service-card-${s.key}`}
                    className="grid grid-cols-1 md:grid-cols-12 min-h-[400px] md:min-h-[440px]"
                  >
                    {/* LEFT: text card (40%) */}
                    <div className="md:col-span-5 bg-white/80 backdrop-blur p-6 md:p-10 lg:p-12 flex flex-col justify-center relative">
                      <div className="max-w-md relative z-10">
                        <div className="flex items-center gap-3">
                          <div className="w-11 h-11 rounded-2xl bg-green-100 grid place-items-center">
                            <Icon className="w-5 h-5 text-green-700" strokeWidth={2.2} />
                          </div>
                          <span className="inline-flex items-center gap-1 bg-orange-50 text-orange-700 border border-orange-200 rounded-full px-3 py-1 text-[11px] font-bold tracking-widest uppercase">
                            {s.highlight}
                          </span>
                        </div>
                        <h3 className="mt-4 font-display font-black text-[26px] md:text-[32px] lg:text-[38px] leading-[1.06] text-stone-900 tracking-tight">
                          {s.title}
                        </h3>
                        <p className="mt-3 text-stone-600 leading-relaxed text-[14.5px]">
                          {s.desc}
                        </p>

                        {bullets && (
                          <ul className="mt-4 space-y-1.5">
                            {bullets.map((b, bi) => (
                              <li
                                key={bi}
                                className="flex items-start gap-2.5 text-stone-700 text-[13.5px]"
                              >
                                <span className="w-4.5 h-4.5 rounded-full bg-green-100 grid place-items-center flex-shrink-0 mt-0.5">
                                  <Check className="w-3 h-3 text-green-700" strokeWidth={3} />
                                </span>
                                <span>{b}</span>
                              </li>
                            ))}
                          </ul>
                        )}

                        <div className="mt-5 flex flex-wrap items-center gap-3">
                          <Button
                            onClick={() => handleCta(s)}
                            data-testid={`service-cta-${s.key}`}
                            className="btn-premium rounded-full bg-orange-500 hover:bg-orange-600 text-white h-11 px-6 font-semibold"
                          >
                            {DEEP_LINKS[s.key]
                              ? t("services.cta_explore")
                              : t("services.cta")}
                            <ArrowRight className="ml-1.5 w-4 h-4" />
                          </Button>
                          {s.key === "street" ? (
                            <button
                              type="button"
                              onClick={() => onRequestQuote?.({ source: "service_street_b2b" })}
                              data-testid="service-cta-street-b2b"
                              className="text-sm font-semibold text-green-700 underline-offset-4 hover:underline"
                            >
                              {t("services.cta_street_b2b")}
                            </button>
                          ) : (
                            <a
                              href="/#calculator"
                              className="text-sm font-semibold text-green-700 underline-offset-4 hover:underline"
                            >
                              {t("hero.cta_calc")}
                            </a>
                          )}
                        </div>

                        <div className="absolute bottom-3 right-5 font-handwritten text-6xl md:text-7xl text-stone-100/90 select-none pointer-events-none">
                          0{i + 1}
                        </div>
                      </div>
                    </div>

                    {/* RIGHT: full-bleed image (60%) */}
                    <div className="md:col-span-7 relative min-h-[240px] md:min-h-full bg-stone-100 slide-img-vignette">
                      <img
                        src={img}
                        alt={s.title}
                        className="absolute inset-0 w-full h-full object-cover"
                        loading={i === 0 ? "eager" : "lazy"}
                        onError={(e) => {
                          e.currentTarget.style.display = "none";
                        }}
                      />
                      <div className="absolute inset-y-0 left-0 w-32 bg-gradient-to-r from-white/85 via-white/30 to-transparent hidden md:block" />
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Progress bar */}
          <div className="absolute inset-x-0 bottom-0 h-[3px] bg-stone-100/70">
            <div
              key={idx + "_" + paused}
              className="h-full bg-orange-500"
              style={{
                width: paused ? `${((idx + 1) / items.length) * 100}%` : "100%",
                transition: paused ? "none" : `width ${AUTO_MS}ms linear`,
              }}
            />
          </div>
        </div>

        {/* Tiles row — sits closer to carousel, visible in viewport */}
        <div
          className="mt-5 grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-2"
          data-testid="services-tiles"
        >
          {items.map((s, i) => {
            const Icon = ICONS[s.key] || Home;
            const active = i === idx;
            return (
              <button
                key={s.key}
                onClick={() => goto(i)}
                data-testid={`service-tile-${s.key}`}
                className={`group rounded-2xl border p-3 text-left transition premium-card ${
                  active
                    ? "border-orange-400 bg-white shadow-md"
                    : "border-stone-200 bg-white/70 hover:bg-white hover:border-stone-300"
                }`}
              >
                <div className="flex items-center gap-2">
                  <Icon
                    className={`w-4 h-4 ${active ? "text-orange-600" : "text-green-700"}`}
                    strokeWidth={2.2}
                  />
                  <div
                    className={`text-[11px] uppercase tracking-wider font-semibold ${
                      active ? "text-orange-600" : "text-stone-500"
                    }`}
                  >
                    0{i + 1}
                  </div>
                </div>
                <div
                  className={`mt-1.5 text-xs font-semibold leading-tight ${
                    active ? "text-stone-900" : "text-stone-700"
                  }`}
                >
                  {s.title}
                </div>
              </button>
            );
          })}
        </div>
      </div>
    </section>
  );
}
